import {noop, isEqual} from 'lodash'
import * as React from 'react'
import type {ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent} from 'react'

import {splitProps} from '@base/react-utils'

import type {Feedback} from '@base-uikit/feedback'
import ConfirmationKeys from '@base-uikit/keyboard/ConfirmationKeys'

import ControlledForm from './ControlledForm'
import type {FormAtPathType, FormType} from './createForm'
import {defaultFormDataReducer, reset} from './defaultFormDataReducer'
import type {ChildrenInnerFieldImplementationType} from './FieldController'
import type {RootFieldDataType} from './flags'
import FormLayout, {FormLayoutProps} from './FormLayout'
import {getDataFromValues, cloneCreateOrCallAllNodes} from './util'

export * from './flags'

export type FeedbackType =
  | {}
  | {
      status: 'loading'
    }
  | {
      status: 'success'
    }
  | {
      status: 'error'
      error?: {
        message: string | React.ReactNode | undefined | null
      }
    }
  | Feedback

export type StandardHandlersType = {
  onValueChange?: (value: any) => void
  // Called when user hits [enter] key and field is not multiline
  // TODO: should be 'onConfirm'?
  onSubmit?: (value: any) => void
  // Called when user hits [escape] key
  onCancel?: () => void
  onChange?: (event: ChangeEvent) => void
  onClick?: (event: MouseEvent) => void
  onFocus?: (event: FocusEvent) => void
  onBlur?: (event: FocusEvent) => void
  onKeyDown?: (event: KeyboardEvent) => void
  onKeyUp?: (event: KeyboardEvent) => void
}

export type ValidationResultType =
  | void
  | boolean
  | string
  | React.ReactNode
  | undefined
  | null
export type ValidatorDataType = {
  field: FieldDataType | void // eslint-disable-line @typescript-eslint/no-use-before-define
  fields: FormDataType // eslint-disable-line @typescript-eslint/no-use-before-define
  path: string
}
export type ValidatorTypeArgs = [any, ValidatorDataType]
export type ValidatorType = (
  value: any,
  data: ValidatorDataType
) => ValidationResultType | Promise<ValidationResultType>
export type ValidatorConditionType = (
  value: any,
  data: ValidatorDataType
) => boolean | Promise<boolean>
export type ValidationStarterType = () =>
  | ValidationResultType
  | Promise<ValidationResultType>

export type SizeType = number | string | 'fill' | 'grow' | 'max'
export type StandardComponentPropsType = {
  value?: any
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | 'xxxl'
  shade?: 'dark' | 'dim' | 'default' | 'light' | 'bright'
  disabled?: boolean
  focused?: boolean
  invalid?: boolean
  clearable?: boolean
  readOnly?: boolean
  validationFeedback?: FeedbackType
  width?: SizeType
  height?: SizeType
  siblings?: string // TODO: 'top' | 'right' | 'left' | 'bottom' | 'right,left' | 'top,bottom',
  serializedFields?: unknown
  // TODO: add others
}

export type MapFieldDataType = ValidatorDataType
export type MapFieldMethodType =
  | ((data: MapFieldDataType) => any | void)
  | undefined
  | null

export type FieldDataType = {
  touched?: boolean
  focused?: boolean
} & StandardComponentPropsType

export type ValidateOnType = 'blur' | 'submit' | 'change' | 'always'

export type StandardFieldPropsType = StandardComponentPropsType &
  FormAtPathType &
  ChildrenInnerFieldImplementationType & {
    initialValue?: any
    validateOn?: ValidateOnType
    validator?: ValidatorType | void
    /**
     * pass a string to set the error message for when the field does not have a value
     */
    required?: boolean | string | ValidatorType
    mapField?: MapFieldMethodType
  }

export type PathType = string
export type FormValueMap = {
  [path: string]: any
}
export type FormDataType = {
  FormRootField: RootFieldDataType
} & FormFieldsDataType
export type FormFieldsDataType = Record<string, FieldDataType>
export type ChangeSpecType = any // TODO
export type DispatchActionType = (changeSpec: ChangeSpecType) => void
export type FormOnSubmit = (
  serializedData: FormValueMap,
  form?: FormType,
  options?: any
) => Promise<ValidationResultType> | ValidationResultType
export type FormSerializer = <T>(data: FormFieldsDataType) => T
export type FormDefinitionMethodsType = {
  /**
   * return true, a string error if the submit operation failed, just like validators
   */
  onSubmit?: FormOnSubmit
  /**
   * called whenever escape key is pressed inside the form
   */
  onCancel?: (() => void) | null
  serializer?: FormSerializer
}

export type FormDefinitionType = {
  dispatch: DispatchActionType
  data: any // $Shape<FormDataType> eslint-disable-line @typescript-eslint/no-use-before-define,
  validateWhenDataPreloaded?: boolean
  // Setting this prop will allow Form to scroll to first invalid field
  scrollToInvalidField?: boolean
  withNestedFields?: boolean
}

type OnChangeFormHelpers = {
  dispatch: DispatchActionType
}

export type FormPropsType = {
  children?: React.ReactNode | undefined | null | React.ComponentType<any>
  initialValues?: FormValueMap | null
  disabled?: boolean
  firstValidationAfterSubmit?: boolean
  submitOnEnterKey?: boolean
  onSubmit?: FormOnSubmit
  scrollToInvalidField?: boolean
  submitFeedback?: FeedbackType
  onChange?: (
    data: FormDataType,
    onChangeFormHelpers: OnChangeFormHelpers
  ) => void
  onCancel?: (() => void) | null
  submitKeys?: Array<string>
  withNestedFields?: boolean
} & FormLayoutProps

/**
 * default ControlledForm implementation which holds the state internally
 */
// eslint-disable-next-line react/no-unsafe
export class FormWithState extends React.PureComponent<
  FormPropsType & FormDefinitionMethodsType,
  any
> {
  dispatch = (changeSpec: ChangeSpecType) =>
    !this.unmounted &&
    this.setState(
      ({formState}) => ({
        formState: defaultFormDataReducer(formState, changeSpec),
      }),
      () =>
        this.props.onChange &&
        this.props.onChange(this.state.formState, {dispatch: this.dispatch})
    )
  state: any = this.props.initialValues
    ? {
        formState: getDataFromValues(this.props.initialValues, {
          withNestedFields: this.props.withNestedFields,
        }),
      }
    : {formState: {}}
  unmounted: boolean = false

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.initialValues, nextProps.initialValues)) {
      this.dispatch(
        reset({
          data: getDataFromValues(nextProps.initialValues, {
            withNestedFields: nextProps.withNestedFields,
          }),
        })
      )
    }
  }

  componentWillUnmount(): any {
    this.unmounted = true
  }

  render() {
    const {
      props: {children, onSubmit = noop, ...rest},
    } = this
    // TODO: the dispatch should take care of batching changes (e.g. to a frame)
    return (
      <ControlledForm
        data={this.state.formState}
        dispatch={this.dispatch}
        onSubmit={onSubmit}
        {...rest}
      >
        {children}
      </ControlledForm>
    )
  }
}

export default function Form({
  children,
  submitOnEnterKey = true,
  onCancel,
  submitKeys = ['enter'],
  ...props
}: FormPropsType) {
  const [layoutProps, formProps] = splitProps(props, [
    'className',
    'width',
    'height',
    /^on(Key|Mouse)/,
    /^data-/,
  ])
  return (
    <FormWithState {...formProps}>
      {(rootProps) => (
        <ConfirmationKeys
          onConfirm={submitOnEnterKey ? rootProps.form.trySubmit : null}
          onCancel={onCancel}
          confirmationKeyNames={submitKeys}
        >
          <FormLayout {...layoutProps}>
            {cloneCreateOrCallAllNodes(children, rootProps)}
          </FormLayout>
        </ConfirmationKeys>
      )}
    </FormWithState>
  )
}
